import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { HotKeysService, UtilityService } from '@common/services';
import {
    RallyGameHighScoreModalComponent,
    RallyGameInstructionsModalComponent,
    RallyGameShareModalComponent,
} from '@modules/landing-pages/components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';

@Component({
    selector: 'sbf-rally-game',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './rally-game.component.html',
    styleUrls: ['rally-game.component.scss'],
})
export class RallyGameComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('gameCanvas', { static: false }) gameCanvas!: ElementRef<HTMLCanvasElement>;
    gameCanvasContext!: CanvasRenderingContext2D;

    player!: Player;
    timerSubscription!: Subscription;
    animationFrameHandle!: number;

    perm: number[] = [];

    toBeat: number = 5000;
    refString!: string;
    refStringEncoded!: string;
    sessionHigh = 0;

    globalTime = 0;
    score = 0;
    speed = 1.5;
    playing = true;

    keys: Record<string, number> = {
        ArrowUp: 0,
        ArrowDown: 0,
        ArrowLeft: 0,
        ArrowRight: 0,
    };

    subscription: Subscription = new Subscription();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private hotKeysService: HotKeysService,
        private utilityService: UtilityService,
        private eventManager: EventManager,
        private ngbModalService: NgbModal,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        const passedRef = this.activatedRoute.snapshot.queryParams.ref;

        if (passedRef) {
            this.toBeat = parseInt(this.utilityService.window.atob(passedRef), 10);
        }

        this.utilityService.window.onkeydown = (event) => (this.keys[event.key] = 1);
        this.utilityService.window.onkeyup = (event) => (this.keys[event.key] = 0);
        this.utilityService.window.onresize = (event) => this.setCanvasSize();

        // iOS doesn't support screen.orientation yet. FTW
        // this.utilityService.window.screen.orientation.addEventListener('change', (event) => {
        //     this.setCanvasSize();
        // });
        // So use MQL instead  https://stackoverflow.com/a/44709318
        const mql = window.matchMedia('(orientation: portrait)');
        mql.addEventListener('change', (mediaQueryListEvent) => {
            this.setCanvasSize();
        });

        this.subscription.add(
            this.hotKeysService.addShortcut$({ keys: 'Space' }).subscribe(() => {
                cancelAnimationFrame(this.animationFrameHandle);
                this.timerSubscription.unsubscribe();
                this.restart();
            })
        );

        this.subscription.add(
            this.hotKeysService.addShortcut$({ keys: 'ArrowLeft' }).subscribe(() => {
                this.keys.ArrowLeft = 1;
            })
        );

        this.subscription.add(
            this.hotKeysService.addShortcut$({ keys: 'ArrowRight' }).subscribe(() => {
                this.keys.ArrowRight = 1;
            })
        );

        this.eventManager.addEventListener(
            this.document.body,
            'touchstart',
            (touchEvent: TouchEvent) => {
                // touchEvent.preventDefault();
                const clientWidth = this.utilityService.window.document.body.clientWidth;
                const touches = touchEvent.changedTouches;
                for (let i = 0; i < touches.length; i++) {
                    if (touches[i].pageX >= clientWidth / 2) {
                        this.keys.ArrowRight = 1;
                    } else {
                        this.keys.ArrowLeft = 1;
                    }
                }
                touchEvent.changedTouches.item;
            }
        );
        this.eventManager.addEventListener(
            this.document.body,
            'touchend',
            (touchEvent: TouchEvent) => {
                // touchEvent.preventDefault();
                const clientWidth = this.utilityService.window.document.body.clientWidth;
                const touches = touchEvent.changedTouches;
                for (let i = 0; i < touches.length; i++) {
                    if (touches[i].pageX >= clientWidth / 2) {
                        this.keys.ArrowRight = 0;
                    } else {
                        this.keys.ArrowLeft = 0;
                    }
                }
                touchEvent.changedTouches.item;
            }
        );
    }

    ngAfterViewInit() {
        this._initCanvas();
        this._initGame();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private _initCanvas() {
        this.setCanvasSize();
        const context = this.gameCanvas.nativeElement.getContext('2d');
        if (!context) {
            throw new Error('UNABLE_TO_GET_CONTEXT');
        }
        this.gameCanvasContext = context;
    }

    private _initGame() {
        while (this.perm.length < 255) {
            let randomValue;
            while (this.perm.includes((randomValue = Math.floor(Math.random() * 255))));
            this.perm.push(randomValue);
        }
        this.player = new Player(
            this.gameCanvas,
            this.gameCanvasContext,
            this.noise,
            this.getGlobalTime,
            this.getSpeed,
            this.isPlaying,
            this.setPlaying,
            this.keys
        );
        this.loop(false);
        const {
            result,
        }: {
            componentInstance: RallyGameHighScoreModalComponent;
            result: Promise<unknown>;
        } = this.ngbModalService.open(RallyGameInstructionsModalComponent, {
            size: 'md',
            centered: false,
        });

        result.then(
            (confirm) => {
                this.start();
            },
            (cancel) => {
                this.start();
            }
        );
    }

    setCanvasSize = () => {
        const clientWidth = this.utilityService.window.document.body.clientWidth;
        const clientHeight = this.utilityService.window.document.body.clientHeight;
        // const clientHeightCalculated = clientWidth * (2 / 3);
        const clientHeightCalculated = clientHeight * 0.8;
        let heightToUse: number;
        if (clientHeight < clientHeightCalculated) {
            heightToUse = clientHeight;
        } else {
            heightToUse = clientHeightCalculated;
        }

        this.gameCanvas.nativeElement.width = clientWidth;
        this.gameCanvas.nativeElement.height = heightToUse; // clientWidth * (2 / 3)
    };

    loop = (repeat = true) => {
        // this.speed -= (this.speed - (this.keys.ArrowUp - this.keys.ArrowDown)) * 0.01;
        this.globalTime += 10 * this.speed;
        this.gameCanvasContext.fillStyle = '#19f';
        this.gameCanvasContext.fillRect(
            0,
            0,
            this.gameCanvas.nativeElement.width,
            this.gameCanvas.nativeElement.height
        );

        this.gameCanvasContext.fillStyle = 'rgba(0,0,0,0.25)';
        this.gameCanvasContext.beginPath();
        this.gameCanvasContext.moveTo(0, this.gameCanvas.nativeElement.height);
        for (let i = 0; i < this.gameCanvas.nativeElement.width; i++)
            this.gameCanvasContext.lineTo(
                i,
                this.gameCanvas.nativeElement.height * 0.8 -
                    this.noise(this.globalTime + i * 5) * 0.25
            );
        this.gameCanvasContext.lineTo(
            this.gameCanvas.nativeElement.width,
            this.gameCanvas.nativeElement.height
        );
        this.gameCanvasContext.fill();

        this.gameCanvasContext.fillStyle = '#444';
        this.gameCanvasContext.beginPath();
        this.gameCanvasContext.moveTo(0, this.gameCanvas.nativeElement.height);
        for (let i = 0; i < this.gameCanvas.nativeElement.width; i++)
            this.gameCanvasContext.lineTo(
                i,
                this.gameCanvas.nativeElement.height - this.noise(this.globalTime + i) * 0.25
            );
        this.gameCanvasContext.lineTo(
            this.gameCanvas.nativeElement.width,
            this.gameCanvas.nativeElement.height
        );
        this.gameCanvasContext.fill();

        this.player.draw();
        if (this.player.x < 0) {
            this.gameOver();
        } else {
            this.calculateScore();
            if (repeat) {
                this.animationFrameHandle = requestAnimationFrame(() => this.loop());
            }
        }
    };

    calculateScore = () => {
        // const clientHeight = this.utilityService.window.document.body.clientHeight;

        if (Math.abs(this.player.rSpeed) > 5) {
            return;
        }

        if (Math.abs(this.player.rSpeed) > 2) {
            this.score + 10 * Math.abs(this.player.rSpeed);
            return;
        }
        this.score =
            this.score +
            (100 / Math.abs(Math.ceil(this.player.y) || 1)) *
                (10 * Math.abs(this.player.rSpeed || 0.01));
        this.changeDetectorRef.detectChanges();
    };

    gameOver = () => {
        this.timerSubscription.unsubscribe();

        if (this.score > this.sessionHigh) {
            this.sessionHigh = Math.round(this.score);
        }

        if (this.score > this.toBeat) {
            this.toBeat = Math.round(this.score);
            this.refString = this.utilityService.window.btoa(this.toBeat.toString());
            this.refStringEncoded = encodeURIComponent(this.refString);
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: {
                    ref: this.refString,
                },
                queryParamsHandling: 'merge',
            });
            this.reset();
            this.highScore();
        } else {
            this.restart();
        }
    };

    highScore = () => {
        const {
            componentInstance,
            result,
        }: {
            componentInstance: RallyGameHighScoreModalComponent;
            result: Promise<unknown>;
        } = this.ngbModalService.open(RallyGameHighScoreModalComponent, {
            size: 'md',
            centered: false,
        });

        componentInstance.highScore = this.toBeat;
        componentInstance.refStringEncoded = this.refStringEncoded;

        result.then(
            (confirm) => {
                this.start();
            },
            (cancel) => {
                this.start();
            }
        );
    };

    reset = () => {
        this.keys = {
            ArrowUp: 0,
            ArrowDown: 0,
            ArrowLeft: 0,
            ArrowRight: 0,
        };
        this.globalTime = 0;
        this.score = 0;
        this.speed = 1.5;
        this.playing = true;

        this.player = new Player(
            this.gameCanvas,
            this.gameCanvasContext,
            this.noise,
            this.getGlobalTime,
            this.getSpeed,
            this.isPlaying,
            this.setPlaying,
            this.keys
        );
    };

    restart = () => {
        this.reset();
        this.start();
    };

    start = () => {
        this.loop();
        this.timerSubscription = timer(1000, 1000).subscribe((val) => {
            this.speed = this.speed + 0.01;
        });
    };

    // https://www.gamedev.net/tutorials/programming/general-and-gameplay-programming/a-brief-introduction-to-lerp-r4954/
    lerp = (start: number, end: number, time: number): number => {
        return start + ((end - start) * (1 - Math.cos(time * Math.PI))) / 2;
    };

    noise = (x: number): number => {
        x = (x * 0.01) % 254;
        return this.lerp(this.perm[Math.floor(x)], this.perm[Math.ceil(x)], x - Math.floor(x));
    };

    getGlobalTime = (): number => {
        return this.globalTime;
    };

    getSpeed = (): number => {
        return this.speed;
    };

    isPlaying = (): boolean => {
        return this.playing;
    };

    setPlaying = (playing: boolean): void => {
        this.playing = playing;
    };

    resetHighScore() {
        cancelAnimationFrame(this.animationFrameHandle);
        this.timerSubscription.unsubscribe();
        this.restart();
        this.toBeat = 5000;
        this.sessionHigh = 0;
    }

    share() {
        this.ngbModalService.open(RallyGameShareModalComponent, {
            size: 'md',
            centered: false,
        });
    }
}

class Player {
    x!: number;
    y!: number;
    ySpeed!: number;
    rot!: number;
    rSpeed!: number;
    img!: HTMLImageElement;

    gameCanvas!: ElementRef<HTMLCanvasElement>;
    gameCanvasContext!: CanvasRenderingContext2D;

    noise!: (x: number) => number;
    getGlobalTime!: () => number;
    getSpeed!: () => number;
    isPlaying!: () => boolean;
    setPlaying!: (playing: boolean) => void;
    keys!: Record<string, number>;

    constructor(
        gameCanvas: ElementRef<HTMLCanvasElement>,
        gameCanvasContext: CanvasRenderingContext2D,
        noise: (x: number) => number,
        getGlobalTime: () => number,
        getSpeed: () => number,
        isPlaying: () => boolean,
        setPlaying: (playing: boolean) => void,
        keys: Record<string, number>
    ) {
        this.x = gameCanvas.nativeElement.width / 2;
        this.y = 0;
        this.ySpeed = 0;
        this.rot = 0;
        this.rSpeed = 0;
        this.img = new Image();
        this.img.src = document.getElementsByTagName('template')[0].innerHTML;
        this.gameCanvas = gameCanvas;
        this.gameCanvasContext = gameCanvasContext;
        this.noise = noise;
        this.getGlobalTime = getGlobalTime;
        this.getSpeed = getSpeed;
        this.isPlaying = isPlaying;
        this.setPlaying = setPlaying;
        this.keys = keys;
    }

    // eslint-disable-next-line complexity
    draw = () => {
        const globalTime = this.getGlobalTime();
        const p1 = this.gameCanvas.nativeElement.height - this.noise(globalTime + this.x) * 0.25;
        const p2 =
            this.gameCanvas.nativeElement.height - this.noise(globalTime + 5 + this.x) * 0.25;

        let grounded = 0;

        if (p1 - 12 > this.y) {
            this.ySpeed += 0.1;
        } else {
            this.ySpeed -= this.y - (p1 - 12);
            this.y = p1 - 12;
            grounded = 1;
        }

        const angle = Math.atan2(p2 - 12 - this.y, this.x + 5 - this.x);
        this.y += this.ySpeed;

        if (!this.isPlaying() || (grounded && Math.abs(this.rot) > Math.PI * 0.5)) {
            this.setPlaying(false);
            this.rSpeed = 5;
            this.keys.ArrowUp = 1;
            this.x -= this.getSpeed() * 5;
        }

        if (grounded && this.isPlaying()) {
            this.rot -= (this.rot - angle) * 0.65;
            this.rSpeed = this.rSpeed - (angle - this.rot);
        }
        this.rSpeed += (this.keys.ArrowLeft - this.keys.ArrowRight) * 0.05;
        this.rot -= this.rSpeed * 0.1;
        if (this.rot > Math.PI) this.rot = -Math.PI;
        if (this.rot < -Math.PI) this.rot = Math.PI;
        this.gameCanvasContext.save();
        this.gameCanvasContext.translate(this.x, this.y - 3);
        this.gameCanvasContext.rotate(this.rot);
        this.gameCanvasContext.drawImage(this.img, -25, -15, 50, 30);
        this.gameCanvasContext.restore();
    };
}
